import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  BaseButton,
  BottomSheet,
  Box,
  HStack,
  IconButton,
  pickColor,
  Text,
  VStack,
} from 'remodeling-web-mobile-sdk-ui';
import styled from 'styled-components';
import useClipboard from '@/hooks/useClipboard';
import { getAsset } from '@/utils';
import { useAlert } from '../AlertProvider';
import { useWebApp } from '../WebAppProvider';
import TrackingService from '@/services/tracking';

type ShareData = { url: string };
type HandleOpen = ({ url }: ShareData) => void;

declare const window: any;

const ShareContext = createContext({
  handleOpen: (_) => {},
} as {
  handleOpen: HandleOpen;
});

export const useShare = () => {
  const context = useContext(ShareContext);

  if (context === undefined) {
    throw new Error('useShare must be used within a ShareContext');
  }

  return context;
};

export interface ShareProviderProps {
  children: ReactNode | ReactNode[];
}

const ShareProvider = ({ children }: ShareProviderProps) => {
  const { isApp, handleShare, handleFireNativeBack, detachCallback } =
    useWebApp();

  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<ShareData>();

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback<HandleOpen>(
    (data) => {
      if (isApp()) {
        handleShare(data!.url);
      } else {
        setData(data);
        setIsOpen(true);

        handleFireNativeBack('FireNativeBack-ShareProvider', async () => {
          handleClose();
        });
      }
    },
    [handleClose, handleFireNativeBack, handleShare, isApp],
  );

  useEffect(() => {
    if (!isOpen) {
      detachCallback('FireNativeBack-ShareProvider');
    }
  }, [detachCallback, isOpen]);

  const { handleCopy: copyClipboard } = useClipboard();

  const { handleOpen: openAlert } = useAlert();

  const handleShareKakao = useCallback(() => {
    if (typeof window !== 'undefined' && window.Kakao) {
      window.Kakao.Share.sendScrap({
        requestUrl: data!.url,
        installTalk: true,
      });
    }
  }, [data]);

  const handleShareFacebook = useCallback(() => {
    window.open(`http://www.facebook.com/sharer/sharer.php?u=${data!.url}`);
  }, [data]);

  const handleShareTwitter = useCallback(() => {
    window.open(`https://twitter.com/intent/tweet?url=${data!.url}`);
  }, [data]);

  const handleShareBand = useCallback(() => {
    window.open(
      `http://band.us/plugin/share?body=${data!.url}&route=${
        process.env.NEXT_PUBLIC_MWEB_HOST?.split('https://')[1]
      }`,
    );
  }, [data]);

  const handleShareLine = useCallback(() => {
    window.open(`http://line.me/R/msg/text/?${data!.url}`);
  }, [data]);

  const handleShareSms = useCallback(() => {
    window.open(`sms:?&body=${data!.url}`);
  }, [data]);

  const handleShareUrl = useCallback(() => {
    copyClipboard(data!.url);
    openAlert({
      type: 'alert',
      textOptions: {
        title: '주소가 복사되었습니다.',
      },
    });
  }, [copyClipboard, data, openAlert]);

  return (
    <ShareContext.Provider value={{ handleOpen }}>
      {children}
      <BottomSheet isOpen={isOpen} handleClose={handleClose}>
        <Box m={0} position="relative" {...TrackingService.tagGTM('share')}>
          <VStack alignment="start" width="100%">
            <Text
              variant="subTitle1"
              color={pickColor('accent_color.primary.default')}
              width="100%"
              pt={24}
              pl={20}
              pr={20}
              pb={15}
              mb={12}
              borderBottomStyle="solid"
              borderBottomWidth={1}
              borderBottomColor="#efefef"
            >
              공유하기
            </Text>
            <VStack width="100%" pb={16}>
              <HStack alignment="center">
                <ShareButton
                  src={getAsset('/bt_share_kakao.png')}
                  label="카카오톡"
                  handleClick={handleShareKakao}
                  {...TrackingService.tagGTM('share_button', {
                    'menu-value': 'kakaotalk',
                  })}
                />
                <ShareButton
                  src={getAsset('/bt_share_facebook.png')}
                  label="페이스북"
                  handleClick={handleShareFacebook}
                  {...TrackingService.tagGTM('share_button', {
                    'menu-value': 'facebook',
                  })}
                />
                <ShareButton
                  src={getAsset('/bt_share_twitter.png')}
                  label="트위터"
                  handleClick={handleShareTwitter}
                  {...TrackingService.tagGTM('share_button', {
                    'menu-value': 'twitter',
                  })}
                />
              </HStack>
              <HStack alignment="center">
                <ShareButton
                  src={getAsset('/bt_share_band.png')}
                  label="밴드"
                  handleClick={handleShareBand}
                  {...TrackingService.tagGTM('share_button', {
                    'menu-value': 'band',
                  })}
                />
                <ShareButton
                  src={getAsset('/bt_share_line.png')}
                  label="라인"
                  handleClick={handleShareLine}
                  {...TrackingService.tagGTM('share_button', {
                    'menu-value': 'line',
                  })}
                />
                <ShareButton
                  src={getAsset('/bt_share_sms.png')}
                  label="SMS"
                  handleClick={handleShareSms}
                  {...TrackingService.tagGTM('share_button', {
                    'menu-value': 'sms',
                  })}
                />
                <ShareButton
                  src={getAsset('/bt_share_url.png')}
                  label="URL"
                  handleClick={handleShareUrl}
                  {...TrackingService.tagGTM('share_button', {
                    'menu-value': 'url',
                  })}
                />
              </HStack>
            </VStack>
          </VStack>
          <IconButton
            variant="ic_close_bold_32"
            onClick={handleClose}
            position="absolute"
            top={16}
            right={12}
            p={0}
            {...TrackingService.tagGTM('share_close_button')}
          />
        </Box>
      </BottomSheet>
    </ShareContext.Provider>
  );
};

export default ShareProvider;

const ShareButton = ({
  src,
  label,
  handleClick,
  ...props
}: {
  src: string;
  label: string;
  handleClick: () => void;
}) => {
  return (
    <BaseButton
      pt={12}
      pl={14}
      pr={14}
      pb={10}
      onClick={handleClick}
      {...props}
    >
      <VStack space={11} width="100%">
        <ButtonImage src={src} />
        <Text
          variant="button_md_r"
          color={pickColor('accent_color.primary.default')}
        >
          {label}
        </Text>
      </VStack>
    </BaseButton>
  );
};

const ButtonImage = styled.img`
  width: 60px;
  height: 60px;
`;
